import { LineItem, ServiceFeeState, InvoiceCalculations, InvoiceAppliesTo } from "./types";

/**
 * Standardized draft sanitizer for every "amount" input in Create Invoice.
 * Keeps only digits + one decimal dot so users can clear/retype naturally.
 */
export const formatInvoiceAppliesToNote = (appliesTo?: InvoiceAppliesTo | null): string | undefined => {
  if (!appliesTo) return undefined;
  const label = String(appliesTo.label ?? "").trim();
  if (label) return `Applies to ${label}`;
  if (appliesTo.type === "service") return "Applies to a specific service";
  if (appliesTo.type === "extra_line_item") return "Applies to an extra line item";
  if (appliesTo.type === "invoice") return "Applies to entire invoice";
  return undefined;
};

export const extraLineItemApplyToKey = (id: string) => `extra:${id}`;

export const isExtraLineItemApplyToKey = (key?: string) => Boolean(key?.startsWith("extra:"));

export const resolveApplyToBaseAmount = (
  appliesToType: string | undefined,
  appliesToKey: string | undefined,
  invoiceBase: number,
  amountByKey: Record<string, number>,
): number => {
  const key = appliesToKey || "invoice";
  if (!appliesToType || appliesToType === "invoice" || key === "invoice") {
    return Math.max(0, invoiceBase);
  }
  if (Object.prototype.hasOwnProperty.call(amountByKey, key)) {
    const value = Number(amountByKey[key]);
    return Number.isFinite(value) ? Math.max(0, value) : 0;
  }
  return 0;
};

type ApplyToDraft = {
  id: string;
  kind: "amount" | "percentage";
  value: string;
  appliesToType?: string;
  appliesToKey?: string;
};

const roundAdjustmentMoney = (value: number): number => Math.round(value * 100) / 100;

export const computeAdjustmentAmount = (kind: string | undefined, rawValue: string | number, base: number): number => {
  const value = typeof rawValue === "number" ? rawValue : Number.parseFloat(String(rawValue || "0")) || 0;
  if (kind === "percentage") {
    return roundAdjustmentMoney((Math.max(0, base) * Math.min(Math.max(0, value), 100)) / 100);
  }
  return roundAdjustmentMoney(Math.max(0, value));
};

export const computeDraftAdjustmentAmounts = (
  items: ApplyToDraft[],
  invoiceBase: number,
  amountByKey: Record<string, number>,
): Record<string, number> => {
  const map: Record<string, number> = {};
  items.forEach((item) => {
    const base = resolveApplyToBaseAmount(item.appliesToType, item.appliesToKey, invoiceBase, amountByKey);
    map[item.id] = computeAdjustmentAmount(item.kind, item.value, base);
  });
  return map;
};

export const amountByKeyAfterItemDiscounts = (
  amountByKey: Record<string, number>,
  discounts: ApplyToDraft[],
  discountAmounts: Record<string, number>,
): Record<string, number> => {
  const next = { ...amountByKey };
  discounts.forEach((item) => {
    const key = item.appliesToKey || "invoice";
    if (key === "invoice" || !Object.prototype.hasOwnProperty.call(next, key)) return;
    next[key] = roundAdjustmentMoney(Math.max(0, (next[key] ?? 0) - (discountAmounts[item.id] ?? 0)));
  });
  return next;
};

export const sanitizeAmountDraft = (raw: string): string => {
  if (!raw) return "";
  const cleaned = raw.replace(/[^\d.]/g, "");
  const dotIndex = cleaned.indexOf(".");
  if (dotIndex < 0) return cleaned;
  return `${cleaned.slice(0, dotIndex + 1)}${cleaned.slice(dotIndex + 1).replace(/\./g, "")}`;
};

export const calculateInvoiceTotals = (
  lineItems: LineItem[],
  serviceFee: ServiceFeeState,
  taxRate: number = 0.08,
): InvoiceCalculations => {
  const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);

  const serviceFeeAmount =
    serviceFee.type === "$" ? Number(serviceFee.value) || 0 : (subtotal * (Number(serviceFee.value) || 0)) / 100;

  const total = subtotal + serviceFeeAmount;

  return {
    subtotal,
    serviceFeeAmount,
    total,
  };
};
